import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactPopup from '../components/ContactPopup';

const TermsConditions = () => {
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);

  const handleContactClick = () => {
    setIsContactPopupOpen(true);
  };

  useEffect(() => {
    // Set meta title and description
    document.title = 'Terms & Conditions | Trademark My Brand Name';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Terms and Conditions for Trademark My Brand Name trademark registration services. Review our service agreement, fees, refund policy, and client responsibilities.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Terms and Conditions for Trademark My Brand Name trademark registration services. Review our service agreement, fees, refund policy, and client responsibilities.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);

  return (
    <>
      <Navbar onContactClick={handleContactClick} />
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/">
              <Button variant="outline" className="mb-6">
                <ArrowLeft className="mr-2" size={18} />
                Back to Home
              </Button>
            </Link>

          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <h1 className="text-4xl font-bold text-blue-900 mb-4">Terms & Conditions</h1>

            <div className="space-y-6 text-gray-700 mt-8">
              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">1. Agreement to Terms</h2>
                <p className="leading-relaxed">
                  By accessing or using the services provided by Trademark My Brand Name, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not use our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">2. Services Description</h2>
                <p className="leading-relaxed mb-3">
                  Trademark My Brand Name provides trademark registration services, including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Trademark search and clearance analysis</li>
                  <li>USPTO trademark application preparation and filing</li>
                  <li>Office action response services</li>
                  <li>Trademark monitoring and renewal services</li>
                  <li>Brand protection consultation</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Our services are limited to trademark registration matters. We do not provide general legal advice or representation in litigation.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">3. Fees and Payment</h2>
                <p className="leading-relaxed mb-3">
                  Service fees are clearly displayed in our pricing packages. Additional terms:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Service fees are separate from USPTO government filing fees</li>
                  <li>Payment is required before filing your application</li>
                  <li>Government filing fees are non-refundable once paid to the USPTO</li>
                  <li>Service fees may be refundable as outlined in our refund policy</li>
                  <li>Additional fees may apply for office action responses and amendments</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">4. Client Responsibilities</h2>
                <p className="leading-relaxed mb-3">
                  As a client, you agree to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate and complete information for your trademark application</li>
                  <li>Respond promptly to our requests for additional information</li>
                  <li>Review all documents before submission to the USPTO</li>
                  <li>Use your trademark in commerce as specified in your application</li>
                  <li>Notify us of any changes to your contact information</li>
                  <li>Comply with all USPTO rules and regulations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">5. No Guarantee of Registration</h2>
                <p className="leading-relaxed">
                  While we make every effort to ensure successful registration, we cannot guarantee that the USPTO will approve your trademark application. Registration depends on many factors including trademark availability, distinctiveness, and compliance with USPTO requirements. The final decision rests with the USPTO examining attorney.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">6. Intellectual Property</h2>
                <p className="leading-relaxed">
                  All content on our website, including text, graphics, logos, and software, is the property of Trademark My Brand Name and protected by copyright and trademark laws. You may not use our content without written permission.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">7. Confidentiality</h2>
                <p className="leading-relaxed">
                  We maintain the confidentiality of all client information in accordance with professional standards and applicable laws. Information you provide will only be disclosed as necessary for trademark registration purposes or as required by law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">8. Limitation of Liability</h2>
                <p className="leading-relaxed">
                  To the maximum extent permitted by law, Trademark My Brand Name shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services. Our total liability shall not exceed the amount paid for our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">9. Refund Policy</h2>
                <p className="leading-relaxed mb-3">
                  Our refund policy is as follows:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Full refund available before your application is filed with the USPTO</li>
                  <li>After filing, service fees may be partially refundable based on work completed</li>
                  <li>USPTO government filing fees are never refundable once submitted</li>
                  <li>Refund requests must be submitted in writing</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">10. Termination</h2>
                <p className="leading-relaxed">
                  Either party may terminate the service agreement with written notice. Upon termination, you will be responsible for payment of services rendered up to the termination date.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">11. Governing Law</h2>
                <p className="leading-relaxed">
                  These Terms and Conditions are governed by the laws of the State of California and the United States. Any disputes shall be resolved in the courts of California.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">12. Changes to Terms</h2>
                <p className="leading-relaxed">
                  We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to our website. Continued use of our services constitutes acceptance of modified terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">13. Contact Information</h2>
                <p className="leading-relaxed mb-3">
                  For questions about these Terms and Conditions, please contact us:
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="font-semibold">Trademark My Brand Name</p>
                  <p>15233 Ventura Blvd, Sherman Oaks, CA 91403</p>
                  <p>Phone: +1 (123) 123-4567</p>
                  <p>Email: info@trademarkmybrandname.com</p>
                </div>
              </section>

              <section className="border-t pt-6 mt-6">
                <p className="text-sm text-gray-600 italic">
                  By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
      </div>
      <Footer />
      <ContactPopup 
        open={isContactPopupOpen} 
        onOpenChange={setIsContactPopupOpen} 
      />
    </>
  );
};

export default TermsConditions;
